import { Link, useLocation } from "wouter";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { NAV_ITEMS, NAV_SECTIONS } from "@shared/constants";
import { UserProfile } from "@shared/types";
import { useOrganization } from "@/contexts/OrganizationContext";

interface SidebarProps {
  user: UserProfile;
  isOpen: boolean;
  desktopCollapsed?: boolean;
  onClose: () => void;
  onLogout: () => void;
}

export function Sidebar({ user, isOpen, desktopCollapsed = false, onClose, onLogout }: SidebarProps) {
  const [location] = useLocation();
  const navRef = useRef<HTMLDivElement | null>(null);
  const { currentOrganization, currentOrganizationId } = useOrganization();
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showQuickAccess, setShowQuickAccess] = useState(false);
  const myProfileHref = `/profile/${user.id}${currentOrganizationId ? `?organizationId=${currentOrganizationId}` : ""}`;

  // Preserve sidebar scroll position across route changes
  useEffect(() => {
    const key = 'sidebarScrollTop';
    const saved = sessionStorage.getItem(key);
    if (saved && navRef.current) {
      navRef.current.scrollTop = parseInt(saved, 10) || 0;
    }
    const el = navRef.current;
    const handleScroll = () => {
      if (el) sessionStorage.setItem(key, String(el.scrollTop));
    };
    el?.addEventListener('scroll', handleScroll);
    return () => el?.removeEventListener('scroll', handleScroll);
  }, []);

  // ESC to close settings and quick access panels
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowSettingsMenu(false);
        setShowQuickAccess(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Group navigation items by section
  const personalNavItems = Object.values(NAV_ITEMS)
    .filter((item) => item.section === NAV_SECTIONS.PERSONAL)
    .sort((a: any, b: any) => (a.order || 999) - (b.order || 999));
  const mainNavItems = Object.values(NAV_ITEMS).filter(
    (item) => item.section === NAV_SECTIONS.MAIN
  );
  const successNavItems = Object.values(NAV_ITEMS).filter(
    (item) => item.section === NAV_SECTIONS.MANAGEMENT || item.section === NAV_SECTIONS.SUCCESS
  );
  const servicesNavItems = Object.values(NAV_ITEMS).filter(
    (item) => item.section === NAV_SECTIONS.SERVICES
  );

  return (
    <>
      {isOpen && <button type="button" aria-label="Close navigation backdrop" onClick={onClose} className="fixed inset-0 z-40 bg-black/40 lg:hidden" />}
      <aside
        className={cn(
          "bg-white dark:bg-neutral-800 border-r border-gray-200 dark:border-neutral-700 w-64 h-full z-50 lg:z-30 fixed transition-transform duration-300 ease-in-out",
          isOpen ? "visible translate-x-0" : "invisible -translate-x-full",
          desktopCollapsed ? "lg:invisible lg:-translate-x-full" : "lg:visible lg:translate-x-0"
        )}
      >
        <button type="button" aria-label="Close navigation" onClick={onClose} className="lg:hidden flex h-11 w-11 items-center justify-center ml-auto text-xl">×</button>
        {/* Company / Organization Header with Gear */}
        <div className="px-3 py-1 flex items-center">
          <h1 className="text-lg font-medium flex-1 truncate mr-2" title={currentOrganization?.name || ''}>
            {currentOrganization?.name || 'Organization'}
          </h1>
          <button
            aria-label="Open settings"
            onClick={() => { setShowQuickAccess(false); setShowSettingsMenu(v => !v); }}
            className={cn(
              "text-gray-500 hover:text-primary transition-transform duration-300"
            )}
          >
            <span className={cn(
              "mdi mdi-cog text-4xl text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.9)]",
              showSettingsMenu ? "bolt-rotate" : ""
            )}></span>
          </button>
        </div>

        {/* User Profile Summary */}
        <div className="px-3 py-1 border-b border-gray-200 dark:border-neutral-700">
          <Link
            href={myProfileHref}
            onClick={onClose}
            className="block rounded-md p-1 -m-1 hover:bg-gray-100 dark:hover:bg-neutral-700"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center">
                <span className="mdi mdi-account"></span>
              </div>
              <div className="ml-3">
                <p className="font-medium text-sm">{user.fullName}</p>
                <p className="text-xs text-gray-500 dark:text-neutral-400">{user.role}</p>
              </div>
            </div>
          </Link>
          {/* Removed extra product label to save space */}
        </div>

        {/* Navigation Menu */}
        <nav ref={navRef} className="pt-4 pb-24 overflow-y-auto" style={{ height: 'calc(100% - 170px)' }}>
          {/* Personal Section */}
          <div className="px-4 mb-2 text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">
            My Workspace
          </div>
          {/* Personal items up to and including My Agenda */}
          {(() => {
            const agendaIndex = personalNavItems.findIndex((i: any) => i.path === '/agenda');
            const before = agendaIndex >= 0 ? personalNavItems.slice(0, agendaIndex + 1) : personalNavItems;
            const after = agendaIndex >= 0 ? personalNavItems.slice(agendaIndex + 1) : [];
            return (
              <>
                {before.map((item) => (
                  <Link
                    key={item.path}
                    href={item.path}
                    onClick={onClose}
                    className={cn(
                      "flex items-center px-4 py-2 text-sm",
                      (location === item.path && !showQuickAccess)
                        ? "bg-primary/10 border-l-4 border-primary pl-3 font-medium"
                        : "text-gray-700 hover:bg-gray-200 dark:text-neutral-300 dark:hover:bg-neutral-700"
                    )}
                  >
                    <span className={`mdi mdi-${item.icon} text-lg mr-3`}></span>
                    <span className="min-w-0 flex-1">{item.name.replace(/\s*\(\s*Coming Soon\s*\)/i, '')}</span>{/Coming Soon/i.test(item.name) && <span className="ml-1 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-neutral-700 dark:text-neutral-200">Soon</span>}
                  </Link>
                ))}

                {/* Quick Access right under My Agenda */}
                <button
                  aria-label="Open quick access"
                  onClick={() => { setShowSettingsMenu(false); setShowQuickAccess(v => !v); }}
                  className={cn(
                    "w-full text-left flex items-center justify-between px-4 py-2 text-sm",
                    showQuickAccess ? "bg-primary/10 border-l-4 border-primary pl-3 font-medium" : "text-gray-700 hover:bg-gray-200 dark:text-neutral-300 dark:hover:bg-neutral-700"
                  )}
                >
                  <span className="mr-2">Quick Access</span>
                  <span className={cn(
                    "mdi mdi-flash-outline text-4xl text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.9)]",
                    showQuickAccess ? "animate-bounce" : ""
                  )}></span>
                </button>

                {after.map((item) => (
                  <Link
                    key={item.path}
                    href={item.path}
                    onClick={onClose}
                    className={cn(
                      "flex items-center px-4 py-2 text-sm",
                      (location === item.path && !showQuickAccess)
                        ? "bg-primary/10 border-l-4 border-primary pl-3 font-medium"
                        : "text-gray-700 hover:bg-gray-200 dark:text-neutral-300 dark:hover:bg-neutral-700"
                    )}
                  >
                    <span className={`mdi mdi-${item.icon} text-lg mr-3`}></span>
                    <span className="min-w-0 flex-1">{item.name.replace(/\s*\(\s*Coming Soon\s*\)/i, '')}</span>{/Coming Soon/i.test(item.name) && <span className="ml-1 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-neutral-700 dark:text-neutral-200">Soon</span>}
                  </Link>
                ))}
              </>
            );
          })()}

          {/* Main Section */}
          <div className="px-4 mb-2 mt-1 pt-4 border-t border-gray-200 dark:border-neutral-700 text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">
            Main
          </div>
          {mainNavItems.map((item) => (
            <div key={item.path}>
              <Link
                href={item.path}
                onClick={onClose}
                className={cn(
                  "flex items-center px-4 py-2 text-sm",
                  (location === item.path && !showQuickAccess)
                    ? "bg-primary/10 border-l-4 border-primary pl-3 font-medium"
                    : "text-gray-700 hover:bg-gray-200 dark:text-neutral-300 dark:hover:bg-neutral-700"
                )}
              >
                <span className={`mdi mdi-${item.icon} text-lg mr-3`}></span>
                <span className="min-w-0 flex-1">{item.name.replace(/\s*\(\s*Coming Soon\s*\)/i, '')}</span>{/Coming Soon/i.test(item.name) && <span className="ml-1 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-neutral-700 dark:text-neutral-200">Soon</span>}
              </Link>
              {/* Quick Access button removed from Main section */}
            </div>
          ))}

          {/* Success Section */}
          <div className="px-4 my-2 mt-1 pt-4 border-t border-gray-200 dark:border-neutral-700 text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">
            Success
          </div>
          {successNavItems.map((item) => (
            <Link
              key={item.path}
              href={item.path}
              onClick={onClose}
              className={cn(
                "flex items-center px-4 py-2 text-sm",
                (location === item.path && !showQuickAccess)
                  ? "bg-primary/10 border-l-4 border-primary pl-3 font-medium"
                  : "text-gray-700 hover:bg-gray-200 dark:text-neutral-300 dark:hover:bg-neutral-700"
              )}
            >
              <span className={`mdi mdi-${item.icon} text-lg mr-3`}></span>
              <span className="min-w-0 flex-1">{item.name.replace(/\s*\(\s*Coming Soon\s*\)/i, '')}</span>{/Coming Soon/i.test(item.name) && <span className="ml-1 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-neutral-700 dark:text-neutral-200">Soon</span>}
            </Link>
          ))}

          {/* White-Label Services Section */}
          <div className="px-4 my-2 mt-1 pt-4 border-t border-gray-200 dark:border-neutral-700 text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wider">
            White-Label Services
          </div>
          {servicesNavItems.map((item) => (
            <Link
              key={item.path}
              href={item.path}
              onClick={onClose}
              className={cn(
                "flex items-center px-4 py-2 text-sm",
                (location === item.path && !showQuickAccess)
                  ? "bg-primary/10 border-l-4 border-primary pl-3 font-medium"
                  : "text-gray-700 hover:bg-gray-200 dark:text-neutral-300 dark:hover:bg-neutral-700"
              )}
            >
              <span className={`mdi mdi-${item.icon} text-lg mr-3`}></span>
              <span className="min-w-0 flex-1">{item.name.replace(/\s*\(\s*Coming Soon\s*\)/i, '')}</span>{/Coming Soon/i.test(item.name) && <span className="ml-1 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-neutral-700 dark:text-neutral-200">Soon</span>}
            </Link>
          ))}
        </nav>
      </aside>

      {/* Slide-out settings panel (outside the sidebar) */}
      {showSettingsMenu && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:bg-black/20"
            onClick={() => setShowSettingsMenu(false)}
            aria-hidden="true"
          />
          <div
            className="fixed z-50 left-0 lg:left-64 top-0 bottom-0 w-full lg:w-80 bg-white dark:bg-neutral-900 border-l border-gray-200 dark:border-neutral-700 shadow-xl"
            role="dialog"
            aria-label="Settings"
          >
            <div className="h-full overflow-y-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
                <div className="text-sm font-semibold">Settings</div>
                <button aria-label="Close" onClick={() => setShowSettingsMenu(false)} className="text-gray-500 hover:text-primary p-1">
                  <span className="mdi mdi-close text-xl"></span>
                </button>
              </div>

              <div className="px-4 pt-4 pb-2 text-xs font-semibold text-gray-600 dark:text-neutral-300 uppercase tracking-wider">
                {currentOrganization?.name || 'Organization'}
              </div>

              <Link href="/settings" onClick={() => setShowSettingsMenu(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-chart-bar mr-2"></span>
                Usage and Subscriptions
              </Link>
              <Link href="/team" onClick={() => setShowSettingsMenu(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-account-multiple-outline mr-2"></span>
                Users and Groups
              </Link>
              <Link href="/settings/integrations" onClick={() => setShowSettingsMenu(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-puzzle-outline mr-2"></span>
                Integrations (Coming Soon)
              </Link>
              <Link href="/settings/trash" onClick={() => setShowSettingsMenu(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-trash-can-outline mr-2"></span>
                Trash (Coming Soon)
              </Link>

              <div className="px-4 pt-4 pb-2 text-xs font-semibold text-gray-600 dark:text-neutral-300 uppercase tracking-wider">
                {user.fullName}
              </div>
              <Link href={myProfileHref} onClick={() => setShowSettingsMenu(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-account-circle-outline mr-2"></span>
                My Account
              </Link>
              <Link href="/settings/notifications" onClick={() => setShowSettingsMenu(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-bell-outline mr-2"></span>
                Notifications (Coming Soon)
              </Link>
              <Link href="/settings/out-of-office" onClick={() => setShowSettingsMenu(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-calendar-remove-outline mr-2"></span>
                Out of office status (Coming Soon)
              </Link>
              <Link href="/help" onClick={() => setShowSettingsMenu(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-help-circle-outline mr-2"></span>
                Help Desk
              </Link>
              <button
                onClick={() => {
                  setShowSettingsMenu(false);
                  setShowQuickAccess(false);
                  onClose();
                  onLogout();
                }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800"
              >
                <span className="mdi mdi-logout mr-2"></span>
                Logout
              </button>
            </div>
          </div>
        </>
      )}

      {/* Slide-out Quick Access panel (outside the sidebar) */}
      {showQuickAccess && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:bg-black/20"
            onClick={() => setShowQuickAccess(false)}
            aria-hidden="true"
          />
          <div
            className="fixed z-50 left-0 lg:left-64 top-0 bottom-0 w-full lg:w-72 bg-white dark:bg-neutral-900 border-l border-gray-200 dark:border-neutral-700 shadow-xl"
            role="dialog"
            aria-label="Quick Access"
          >
            <div className="h-full overflow-y-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
                <div className="text-sm font-semibold flex items-center gap-2"><span className="mdi mdi-flash-outline text-4xl text-amber-400 animate-bounce drop-shadow-[0_0_8px_rgba(251,191,36,0.9)]"></span> Quick Access</div>
                <button aria-label="Close" onClick={() => setShowQuickAccess(false)} className="text-gray-500 hover:text-primary p-1">
                  <span className="mdi mdi-close text-xl"></span>
                </button>
              </div>

              <div className="px-4 pt-4 pb-2 text-xs font-semibold text-gray-600 dark:text-neutral-300 uppercase tracking-wider">
                Shortcuts
              </div>

              <Link href="/templates" onClick={() => setShowQuickAccess(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-chef-hat mr-2"></span>
                Cheat Sheets
              </Link>
              <Link href="/lab" onClick={() => setShowQuickAccess(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-flask-outline mr-2"></span>
                Lab Tracker
              </Link>
              <Link href="/passwords" onClick={() => setShowQuickAccess(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-key-variant mr-2"></span>
                Passwords
              </Link>
              <Link href="/contacts" onClick={() => setShowQuickAccess(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-account-box-outline mr-2"></span>
                Important Contacts
              </Link>
              <Link href="/referrals" onClick={() => setShowQuickAccess(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-account-arrow-right-outline mr-2"></span>
                Referral Tracker
              </Link>
              <Link href="/shopping-list" onClick={() => setShowQuickAccess(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-cart-outline mr-2"></span>
                Shopping List
              </Link>
              <Link href="/inventory" onClick={() => setShowQuickAccess(false)} className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800">
                <span className="mdi mdi-warehouse mr-2"></span>
                Inventory
              </Link>

              <div className="px-4 pt-4 pb-2 text-xs font-semibold text-gray-600 dark:text-neutral-300 uppercase tracking-wider">
                Favorites
              </div>
              {/* Favorited pages from My Workspace will appear here (coming soon) */}
            </div>
          </div>
        </>
      )}
    </>
  );
}
